import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import OktaAuth, {
  // AccessToken,
  AuthnTransaction,
  SessionObject,
  // Token,
  UserClaims,
} from '@okta/okta-auth-js';
import { BroadcastChannel } from 'broadcast-channel';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  from,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  retryWhen,
  tap,
} from 'rxjs/operators';

import { RequestStatus } from '@patient-ui/shared/enums';
import { EventCode, IBadDebtMetric, Patient } from '@patient-ui/shared/models';
import {
  AuthConfig,
  AuthConfigService,
  EnvironmentService,
} from '@patient-ui/shared-ui/utils';

import * as AuthActions from './auth.actions';
import { authenticateFailure, updateLoginInfoSuccess } from './auth.actions';
import { AuthState } from './auth.reducer';
import { selectAuthenticationStatus } from './auth.selectors';
import { MetricActions } from '../metric/metric.actions';
import { MetricState } from '../metric/metric.reducer';
import * as MoonshotActions from '../moonshot/moonshot.actions';
import { MoonshotState } from '../moonshot/moonshot.reducer';
import { loadLoggedInPatient } from '../patient/patient.actions';

export const EMAIL_CAMPAIGN_SESSION_KEY = 'emailCampaignId';

export interface MoonshotUser {
  enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  oktaAuth!: OktaAuth;
  broadcastChannel = new BroadcastChannel('auth-channel');
  isLoggedInOkta = false;
  scopes = ['openid', 'profile', 'email'];
  sessionID =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  isStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  tokenIsExpired = false;
  private oktaAuthSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  browserInfo = `Browser: ${navigator.userAgent}`;
  private backendLoginFailureFlag = false;
  private readonly MAX_LOGIN_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1500;

  public get isAuthenticated$(): Observable<boolean> {
    return this.oktaAuthSub.asObservable();
  }

  constructor(
    private readonly http: HttpClient,
    private environmentService: EnvironmentService,
    private authConfigService: AuthConfigService,
    private authStore: Store<AuthState>,
    private metricStore: Store<MetricState>,
    private moonshotStore: Store<MoonshotState>,
    private router: Router
  ) {
    this.authStore.select(selectAuthenticationStatus).subscribe((status) => {
      this.isLoggedInOkta = status === RequestStatus.Success;
    });

    // Listen to logout broadcast to keep tabs in sync
    this.broadcastChannel.onmessage = (message) => {
      if (message === 'logout') {
        window.location.reload();
      }
    };

    // Setup okta auth service
    this.authConfigService.authConfig$
      .pipe(filter((config: AuthConfig) => !!Object.keys(config).length))
      .subscribe(async (config: AuthConfig) => {
        this.oktaAuth = new OktaAuth({
          clientId: config.clientId,
          issuer: config.issuer,
          scopes: this.scopes,
          pkce: true,
          tokenManager: {
            autoRenew: false,
            autoRemove: false,
            syncStorage: false,
          },
          ignoreLifetime: true,
        });

        // Login with new auth token
        // this.oktaAuth.tokenManager.on('added', (key, _newToken) => {
        // const originalUri = this.oktaAuth.getOriginalUri();
        // if (key === 'accessToken' && _newToken) {
        //   this.loginPatient(
        //     (_newToken as AccessToken).accessToken,
        //     originalUri
        //   );
        // }
        // });

        this.isStarted.next(true);
      });
  }

  login(
    username: string,
    password: string,
    redirect: string,
    context?: string
  ): Observable<void> {
    // Reset backend login failure flag when user explicitly logs in
    this.backendLoginFailureFlag = false;

    return from(
      this.oktaAuth
        .signInWithCredentials({ username, password })
        .then((t: AuthnTransaction) => {
          this.handleSignInResponse(t, redirect, context);
        })
        .catch(() => {
          this.authStore.dispatch(authenticateFailure());
        })
    );
  }

  private async handleSignInResponse(
    transaction: AuthnTransaction,
    redirect: string,
    context?: string
  ): Promise<void> {
    if (transaction.status !== 'SUCCESS') {
      this.authStore.dispatch(authenticateFailure());
      return;
    }
    this.oktaAuthSub.next(true);
    this.oktaAuth.setOriginalUri(redirect);
    if (context) {
      //For ondemand this will be called
      this.oktaAuth.session.setCookieAndRedirect(
        transaction.sessionToken,
        `${window.location.origin}/callback`
      );
    } else {
      await this.oktaAuth.token.getWithRedirect({
        pkce: true,
        sessionToken: transaction.sessionToken,
        responseType: 'code',
        redirectUri: `${window.location.origin}/callback`,
      });
    }
  }

  async handleExistingSession(session: SessionObject, redirect?: string) {
    if (session) {
      // Check if backend login previously failed to prevent infinite loop
      if (this.backendLoginFailureFlag) {
        this.oktaAuthSub.next(false);
        this.authStore.dispatch(authenticateFailure());
        return;
      }

      if (!sessionStorage.getItem('metricEmail')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sessionStorage.setItem('metricEmail', (session as any).login);
      }
      // any type needed here as inferred session object actually has id
      // prop, but okta auth lib does not have it type yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessionId = (session as any).id as string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userId = (session as any).userId as string;
      const params = new URL(window.location.href).searchParams;
      this.authStore.dispatch(
        AuthActions.saveSession({ token: sessionId, userId: userId })
      );
      if (params?.get('context') && params?.get('redirecturl')) {
        window.location.href = params.get('redirecturl') as string;
        // Stop code execution to prevent accessing anon pages
        await new Promise((resolve) => setTimeout(resolve, 10000));
      } else if (!this.isLoggedInOkta) {
        if (this.haveClearedToken) {
          // If session and no token, try logging in with cookie
          await this.loginPatient(undefined, redirect);
          return;
        }
        if (this.isTokenNeeded) {
          // Otherwise, get token without requiring credentials
          try {
            const resp = await this.oktaAuth.token.getWithoutPrompt({
              responseType: ['token', 'id_token'],
              scopes: this.scopes,
              redirectUri: `${window.location.origin}/callback`,
              prompt: 'none',
            });
            this.oktaAuth.tokenManager.setTokens(resp.tokens);
            await this.loginPatient(
              resp.tokens?.accessToken?.accessToken,
              redirect
            );
          } catch {
            this.authStore.dispatch(authenticateFailure());
          }
        }
      }
    } else {
      this.oktaAuthSub.next(false);
    }
  }

  getUserInfo(): Observable<UserClaims> {
    return from(this.oktaAuth.getUser());
  }

  logoutAPI() {
    // Execute Okta session close and backend logout in parallel for faster logout
    // Both operations are independent and can run simultaneously

    // Okta session close with error handling
    const oktaSessionClose$ = from(this.oktaAuth.session.close()).pipe(
      catchError(() => of(null))
    );

    // Backend logout with error handling
    const backendLogout$ = this.http
      .delete(
        `${this.environmentService.baseUrl}/protected/patients/current/logout`
      )
      .pipe(catchError(() => of(null)));

    // Execute both in parallel and wait for both to complete
    return forkJoin({
      okta: oktaSessionClose$,
      backend: backendLogout$,
    }).pipe(
      tap(() => localStorage.removeItem('selectOpt')),
      map(() => undefined) // Return undefined for compatibility with existing code
    );
  }

  /**
   * Clears the portal's own session cookies (__Secure-PatientPortalToken/Refresh)
   * via the backend WITHOUT closing the Okta session. Used by the SSO entry flow,
   * where the interclient_token is a token-exchange subject_token bound to the
   * Okta session — closing that session (as logoutAPI does via session.close())
   * invalidates the token and makes the /authorize exchange fail with
   * invalid_grant.
   */
  clearPortalCookies(): Observable<undefined> {
    return this.http
      .delete(
        `${this.environmentService.baseUrl}/protected/patients/current/logout`
      )
      .pipe(
        catchError(() => of(null)),
        map(() => undefined)
      );
  }

  logoutAllSessions(): Observable<boolean> {
    this.broadcastChannel.postMessage('logout');
    this.oktaAuthSub.next(false);
    this.oktaAuth.tokenManager.clear();
    return of(true);
  }

  redirect(redirect: string): Observable<unknown> {
    return from(this.router.navigate([redirect]));
  }

  async loginPatient(accessToken?: string, redirect?: string): Promise<void> {
    const token = accessToken?.trim() || this.accessToken;
    const emailCampaignId = sessionStorage.getItem(EMAIL_CAMPAIGN_SESSION_KEY);
    const loginUrl = emailCampaignId
      ? `${
          this.environmentService.baseUrl
        }/protected/patients/current/login?campaign=${encodeURIComponent(
          emailCampaignId
        )}`
      : `${this.environmentService.baseUrl}/protected/patients/current/login`;

    // Reset the backend login failure flag on new login attempt
    this.backendLoginFailureFlag = false;

    try {
      const response = await this.http
        .post(loginUrl, undefined, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? 'Bearer ' + token : '',
          },
          withCredentials: true,
        })
        .pipe(
          retryWhen((errors) =>
            errors.pipe(
              concatMap((error, index) => {
                const retryAttempt = index + 1;

                // Don't retry on 4xx client errors (unauthorized, forbidden, etc.)
                if (error.status >= 400 && error.status < 500) {
                  return throwError(error);
                }

                // Stop retrying after max attempts
                if (retryAttempt >= this.MAX_LOGIN_RETRIES) {
                  return throwError(error);
                }

                // Retry with exponential backoff: 1500ms, 3000ms, 4500ms
                const delayMs = this.RETRY_DELAY_MS * retryAttempt;
                return timer(delayMs);
              })
            )
          )
        )
        .toPromise();
      const payload = <Patient>response;
      const loginEmail = payload?.loginEmail ?? '';
      // Check to see if the user is a moonshot user
      if (loginEmail) {
        this.getMoonshotUser().subscribe(
          (moonshotUser) => {
            // Store moonshot users data
            this.moonshotStore.dispatch(
              MoonshotActions.setMoonshotEnabled({
                enabled: moonshotUser.enabled,
              })
            );
            // Use the primary patient ID instead of LPID
            this.moonshotStore.dispatch(
              MoonshotActions.setUserPatientId({
                patientId: payload.id ? payload.id.toString() : null,
              })
            );
          },
          (error) => {
            if (error.status === 404) {
              this.moonshotStore.dispatch(
                MoonshotActions.setMoonshotEnabled({ enabled: false })
              );
              this.moonshotStore.dispatch(
                MoonshotActions.setUserPatientId({ patientId: null })
              );
            } else {
              console.error('Error fetching moonshot user:', error);
            }
          }
        );
      }
      sessionStorage.setItem('metricEmail', loginEmail);
      if (emailCampaignId) {
        sessionStorage.removeItem(EMAIL_CAMPAIGN_SESSION_KEY);
      }
      this.oktaAuthSub.next(true);
      this.authStore.dispatch(updateLoginInfoSuccess({ payload }));
      this.authStore.dispatch(loadLoggedInPatient({ payload }));
      if (redirect) {
        const qIndex = redirect.indexOf('?');
        if (qIndex >= 0) {
          const path = redirect.substring(0, qIndex);
          const queryParams: { [key: string]: string } = {};
          redirect
            .substring(qIndex + 1)
            .split('&')
            .forEach((param) => {
              const [key, value] = param.split('=');
              if (key) {
                queryParams[decodeURIComponent(key)] = decodeURIComponent(
                  value || ''
                );
              }
            });
          this.router.navigate([path], { queryParams });
        } else {
          this.router.navigate([redirect]);
        }
      } else {
        this.router.navigate(['portal/dashboard']);
      }
    } catch (error) {
      this.logLoginMetric('Login Error --> ' + error + ' <--');
      this.oktaAuthSub.next(false);
      this.authStore.dispatch(AuthActions.authenticateFailure());

      // Set backend login failure flag to prevent infinite loop
      this.backendLoginFailureFlag = true;

      // Set flag in sessionStorage to show error message on landing page
      sessionStorage.setItem('BackendLoginFailure', 'true');

      // Clear Okta session and tokens to prevent guards from re-attempting login
      await this.clearOktaSessionOnBackendFailure();

      sessionStorage.setItem(`originalPath`, this.router.url);
      this.router.navigate(['/landing']);
    }
  }

  logLoginMetric(alertText: string) {
    const badDebtMetric: IBadDebtMetric = {
      dashboardId: 'Login',
      eventCode: EventCode.loginLanded,
      alertMsg:
        alertText +
        ' USER EMAIL ==> ' +
        sessionStorage.getItem('metricEmail') +
        ' <--BROWSER DETAILS--> ' +
        this.browserInfo,
      templateId: 'LoginTemplateID',
      userType: 'Login',
      sessionID: this.sessionID,
    };
    this.metricStore.dispatch(
      MetricActions.logBadDebtMetric({ metrics: badDebtMetric })
    );
  }

  getMoonshotUser(): Observable<MoonshotUser> {
    const url = `${this.environmentService.baseUrl}/protected/patients/current/moonshot/users`;

    return this.http.get<MoonshotUser>(url);
  }

  get accessToken() {
    return this.oktaAuth.tokenManager
      .getTokensSync()
      .accessToken?.accessToken?.trim();
  }

  get haveClearedToken() {
    return (
      new Date().getTime() / 1000 <
      (this.oktaAuth.tokenManager.getTokensSync().accessToken?.expiresAt ?? 0)
    );
  }

  get isTokenNeeded() {
    return (
      this.oktaAuth.tokenManager.getTokensSync().accessToken?.accessToken ===
      undefined
    );
  }

  refreshSession() {
    return from(this.oktaAuth.session.refresh());
  }

  /**
   * Clears Okta session and tokens when backend login fails.
   * This prevents infinite loop where guards detect active Okta session
   * but backend authentication repeatedly fails.
   */
  private async clearOktaSessionOnBackendFailure(): Promise<void> {
    try {
      // Clear tokens from token manager
      this.oktaAuth.tokenManager.clear();

      // Close Okta session
      await this.oktaAuth.session.close();
    } catch (error) {
      // Continue execution even if clearing fails
    }
  }

  /**
   * Checks if backend login has previously failed.
   * Used by guards to prevent infinite retry loops.
   *
   * @returns true if backend login has failed, false otherwise
   */
  hasBackendLoginFailed(): boolean {
    return this.backendLoginFailureFlag;
  }

  /**
   * Resets the backend login failure flag.
   * Should be called when user explicitly initiates a new login attempt.
   */
  resetBackendLoginFailureFlag(): void {
    this.backendLoginFailureFlag = false;
  }

  ngOnDestroy(): void {
    if (this.oktaAuth) {
      this.oktaAuth.stop();
    }
  }
}
